package com.anjuros.website.redesign.sections.trades

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.components.RdLink
import com.anjuros.website.redesign.components.RdPrimaryButton
import com.anjuros.website.redesign.components.RdSection
import com.anjuros.website.redesign.styles.RdH2Style
import com.anjuros.website.redesign.styles.RdH3Style
import com.anjuros.website.redesign.styles.TradesDesignLinkStyle
import com.anjuros.website.redesign.styles.TradesDirectoryStyle
import com.anjuros.website.redesign.styles.TradesEmptyStyle
import com.anjuros.website.redesign.styles.TradesGroupCountStyle
import com.anjuros.website.redesign.styles.TradesGroupHeadStyle
import com.anjuros.website.redesign.styles.TradesGroupStyle
import com.anjuros.website.redesign.styles.TradesGroupTitleStyle
import com.anjuros.website.redesign.styles.TradesPageLinkStyle
import com.anjuros.website.redesign.styles.TradesRowMainStyle
import com.anjuros.website.redesign.styles.TradesRowSideStyle
import com.anjuros.website.redesign.styles.TradesRowStyle
import com.anjuros.website.redesign.styles.TradesRowsStyle
import com.anjuros.website.redesign.util.RdRoute
import com.anjuros.website.redesign.util.RdText
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/**
 * Every trade, grouped by sector. The search only hides rows; they stay in the page,
 * so search engines always see the full list.
 */
@Composable
fun TradesDirectorySection(query: String) {
    val search = query.trim().lowercase()
    val label = LocalText(RdText.TRADES_DIRECTORY_LABEL)
    val matches = TradeGroups.associateWith { group ->
        group.rows.filter { row -> search.isEmpty() || row.searchText().contains(search) }
    }

    RdSection(
        id = "directory",
        wrapModifier = TradesDirectoryStyle.toModifier().attrsModifier { attr("aria-label", label) },
    ) {
        TradeGroups.forEach { group -> GroupView(group, visibleRows = matches.getValue(group)) }
        if (matches.values.all { it.isEmpty() }) EmptyResult()
    }
}

@Composable
private fun TradeRow.searchText(): String =
    (LocalText(keywords) + " " + LocalText(title) + " " + LocalText(text)).lowercase()

@Composable
private fun GroupView(group: TradeGroup, visibleRows: List<TradeRow>) {
    Div(
        TradesGroupStyle.toModifier()
            .id(group.id)
            .attrsModifier { if (visibleRows.isEmpty()) attr("hidden", "") }
            .toAttrs()
    ) {
        Div(TradesGroupHeadStyle.toModifier().toAttrs()) {
            P(TradesGroupCountStyle.toModifier().toAttrs()) {
                Text(LocalText(RdText.TRADES_COUNT).replace("%d", group.rows.size.toString()))
            }
            H2(RdH2Style.toModifier().then(TradesGroupTitleStyle.toModifier()).toAttrs()) { Text(LocalText(group.title)) }
            P { Text(LocalText(group.text)) }
        }
        Ul(TradesRowsStyle.toModifier().toAttrs()) {
            group.rows.forEach { row -> RowView(row, visible = row in visibleRows) }
        }
    }
}

@Composable
private fun RowView(row: TradeRow, visible: Boolean) {
    Li(TradesRowStyle.toModifier().attrsModifier { if (!visible) attr("hidden", "") }.toAttrs()) {
        Div(TradesRowMainStyle.toModifier().toAttrs()) {
            H3(RdH3Style.toModifier().toAttrs()) { Text(LocalText(row.title)) }
            P { Text(LocalText(row.text)) }
        }
        Div(TradesRowSideStyle.toModifier().toAttrs()) {
            if (row.pageLink != null) {
                Span { Text(LocalText(RdText.TRADES_TRADE_PAGE)) }
                RdLink(row.path, TradesPageLinkStyle.toModifier()) {
                    Text(LocalText(row.pageLink))
                    Span({ attr("aria-hidden", "true") }) { Text("→") }
                }
            } else if (row.suggestedDesign != null) {
                Span { Text(LocalText(RdText.TRADES_SUGGESTED)) }
                RdLink(row.path, TradesDesignLinkStyle.toModifier()) { Text(LocalText(row.suggestedDesign)) }
            }
        }
    }
}

@Composable
private fun EmptyResult() {
    Div(TradesEmptyStyle.toModifier().toAttrs()) {
        H3(RdH3Style.toModifier().toAttrs()) { Text(LocalText(RdText.TRADES_EMPTY_TITLE)) }
        P { Text(LocalText(RdText.TRADES_EMPTY_TEXT)) }
        RdPrimaryButton(RdRoute.CONTACT, RdText.TRADES_EMPTY_BUTTON, withArrow = true)
    }
}
